import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type { ComponentProps } from "react";
import { cn } from "./cn";
import { Icon } from "./icon";

// Sizes are target sizes. Comfortable (the default) is at least 44 px high for every size; the compact density (ADR 0016) is 32 to 40 px.
// The focus ring is the global `:focus-visible` outline of `styles/app.css`: it is never turned off here.

const buttonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-md font-semibold whitespace-nowrap no-underline transition-[color,background-color,border-color] disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 data-[loading=true]:cursor-wait [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-link",
        secondary: "bg-pill text-secondary-foreground hover:bg-border",
        outline: "border border-input bg-card text-foreground hover:bg-accent",
        ghost: "text-foreground hover:bg-accent hover:text-accent-foreground",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive-soft hover:text-destructive",
        link: "text-link underline underline-offset-4 hover:no-underline",
        // A quiet action beside an outline one (Confirm, Dispute): a neutral fill and no visible border.
        soft: "border border-transparent bg-neutral-soft text-foreground hover:bg-border",
        // A destructive action that is not the main one (Delete, Reject): a red label and a red outline on white.
        danger:
          "border border-destructive-border bg-card text-destructive hover:bg-destructive-soft",
      },
      size: {
        sm: "h-11 px-3 text-sm compact:h-8",
        md: "h-11 px-4 text-sm compact:h-9",
        lg: "h-12 px-6 text-base compact:h-10",
        icon: "size-11 compact:size-8",
      },
    },
    defaultVariants: { variant: "default", size: "md" },
  },
);

export type ButtonProps = ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> &
  Readonly<{
    /** Renders the child element (for example a router link) with the button look instead of a `<button>`. */
    asChild?: boolean;
    /** A command is running: the button shows a spinner, is `aria-disabled` and `aria-busy`, ignores clicks, and keeps focus. Say what runs in the label ("Saving…"). */
    loading?: boolean;
  }>;

export function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  disabled,
  type,
  children,
  onClick,
  ...props
}: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size }), className);
  if (asChild) {
    return (
      <Slot.Root className={classes} {...props}>
        {children}
      </Slot.Root>
    );
  }
  return (
    <button
      type={type ?? "button"}
      className={classes}
      disabled={disabled}
      // A loading button is NOT `disabled`: a disabled button that has focus drops focus to the page, and a keyboard user loses their place.
      // It is `aria-disabled` and `aria-busy`, stays focusable, and ignores a click or Enter (Enter in a field also clicks the default button).
      aria-disabled={loading ? true : undefined}
      aria-busy={loading ? true : undefined}
      data-loading={loading ? "true" : undefined}
      onClick={(event) => {
        if (loading) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      {...props}
    >
      {loading ? <Icon name="refresh" className="animate-spin" /> : null}
      {children}
    </button>
  );
}
